class EditableLinkInputController {
    public ngModel: string;
    public onUpdate: (params: { value: string }) => void;
    public name: string;
    public ariaLabel: string;
    public required: boolean = false;
    public readonly: boolean = false;

    public isFocused: boolean = false;
    private lastClickTime: number = 0;
    private shouldNavigate: boolean = false;
    private navigationTimeout: any = null;
    private mouseMoved: boolean = false;
    private mouseDownTime: number = 0;

    private inputElement: HTMLInputElement | null = null;
    private boundMouseEnter: (event: MouseEvent) => void;
    private boundMouseLeave: (event: MouseEvent) => void;
    private boundMouseMove: (event: MouseEvent) => void;
    private boundKeyDownForCursor: (event: KeyboardEvent) => void;
    private boundKeyUp: (event: KeyboardEvent) => void;

    /** @ngInject */
    constructor(private $element: ng.IAugmentedJQuery) {
        // Bind event handlers once so they can be properly removed
        this.boundMouseEnter = this.handleMouseEnter.bind(this);
        this.boundMouseLeave = this.handleMouseLeave.bind(this);
        this.boundMouseMove = this.handleMouseMove.bind(this);
        this.boundKeyDownForCursor = this.handleKeyDownForCursor.bind(this);
        this.boundKeyUp = this.handleKeyUp.bind(this);
    }

    public $onInit(): void {
        // Initialize
        // Get reference to the input element
        this.inputElement = this.$element.find('input')[0] as HTMLInputElement;
        
        if (this.inputElement) {
            // Add mouse move listener to detect if user wants to select text
            document.addEventListener('mousemove', () => {
                if (this.navigationTimeout) {
                    this.mouseMoved = true;
                }
            });

            // Handle mouse over/out to update cursor based on Ctrl key state
            this.inputElement.addEventListener('mouseenter', this.boundMouseEnter);
            this.inputElement.addEventListener('mouseleave', this.boundMouseLeave);
            this.inputElement.addEventListener('mousemove', this.boundMouseMove);

            // Add keyboard listeners to detect Ctrl/Cmd key state
            document.addEventListener('keydown', this.boundKeyDownForCursor);
            document.addEventListener('keyup', this.boundKeyUp);
        }
    }

    private handleMouseEnter(event: MouseEvent): void {
        this.updateCursorStyle(event);
    }

    private handleMouseLeave(event: MouseEvent): void {
        if (this.inputElement) {
            this.inputElement.classList.remove('ctrl-held');
        }
    }

    private handleMouseMove(event: MouseEvent): void {
        this.updateCursorStyle(event);
    }

    private updateCursorStyle(event: MouseEvent): void {
        if (this.inputElement && this.ngModel && !this.isFocused) {
            if (event.ctrlKey || event.metaKey) {
                this.inputElement.classList.add('ctrl-held');
            } else {
                this.inputElement.classList.remove('ctrl-held');
            }
        }
    }

    private handleKeyDownForCursor(event: KeyboardEvent): void {
        if ((event.key === 'Control' || event.key === 'Meta') && this.inputElement && this.ngModel && !this.isFocused) {
            // Check if mouse is over the input
            if (this.inputElement.matches(':hover')) {
                this.inputElement.classList.add('ctrl-held');
            }
        }
    }

    private handleKeyUp(event: KeyboardEvent): void {
        if ((event.key === 'Control' || event.key === 'Meta') && this.inputElement) {
            this.inputElement.classList.remove('ctrl-held');
        }
    }

    public $onDestroy(): void {
        // Clean up timeout if component is destroyed
        if (this.navigationTimeout) {
            clearTimeout(this.navigationTimeout);
            this.navigationTimeout = null;
        }
        
        // Remove event listeners
        if (this.inputElement) {
            this.inputElement.removeEventListener('mouseenter', this.boundMouseEnter);
            this.inputElement.removeEventListener('mouseleave', this.boundMouseLeave);
            this.inputElement.removeEventListener('mousemove', this.boundMouseMove);
        }
        document.removeEventListener('keydown', this.boundKeyDownForCursor);
        document.removeEventListener('keyup', this.boundKeyUp);
    }

    public handleFocus(event: any): void {
        // Cancel any pending navigation when user focuses to edit
        this.cancelNavigation();
        this.isFocused = true;
        // Remove ctrl-held class when focused (always show text cursor when editing)
        if (this.inputElement) {
            this.inputElement.classList.remove('ctrl-held');
        }
    }

    public handleBlur(): void {
        this.isFocused = false;
        // Remove ctrl-held class when blurred (will be re-added if Ctrl is held on hover)
        if (this.inputElement) {
            this.inputElement.classList.remove('ctrl-held');
        }
    }

    public handleMouseDown(event: any): void {
        // Always allow normal input behavior (focusing) when clicking
        // Don't prevent default - let the input focus naturally
        
        // If Ctrl+Click or Middle-click, navigate instead of editing
        if ((event.ctrlKey || event.metaKey || event.button === 1) && this.ngModel) {
            const url = this.ngModel.trim();
            if (url) {
                event.preventDefault();
                event.stopPropagation();
                this.navigateToLink(url);
                return;
            }
        }
        
        // Cancel any pending navigation when user clicks
        this.cancelNavigation();
    }

    public handleClick(event: any): void {
        // Cancel any pending navigation when user clicks
        this.cancelNavigation();
    }

    public handleKeyDown(event: any): void {
        // If user presses a key, they want to edit - cancel navigation
        if (this.navigationTimeout) {
            this.cancelNavigation();
        }
        // Also update cursor style if Ctrl/Cmd key is pressed
        if (event.ctrlKey || event.metaKey) {
            this.handleKeyDownForCursor(event as KeyboardEvent);
        }
    }

    private cancelNavigation(): void {
        if (this.navigationTimeout) {
            clearTimeout(this.navigationTimeout);
            this.navigationTimeout = null;
        }
        this.shouldNavigate = false;
        this.mouseMoved = false;
    }

    public handleInputChange(): void {
        // Notify parent of value change
        if (this.onUpdate) {
            this.onUpdate({ value: this.ngModel });
        }
    }

    private navigateToLink(url: string): void {
        // Ensure URL has protocol
        let urlToOpen = url;
        if (!url.match(/^https?:\/\//i)) {
            urlToOpen = 'http://' + url;
        }
        // Open in new tab
        window.open(urlToOpen, '_blank');
    }
}

export const EditableLinkInputComponent = {
    template: require('./editable-link-input.component.html'),
    style: require('./editable-link-input.component.scss'),
    controller: EditableLinkInputController,
    controllerAs: '$ctrl',
    bindings: {
        ngModel: '=',
        onUpdate: '&?',
        name: '@',
        ariaLabel: '@?',
        required: '<?',
        readonly: '<?'
    }
};




// WEBPACK FOOTER //
// ./src/components/editable-link-input/editable-link-input.component.ts